import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface Student {
  id: string;
  name: string;
  rollNo: string;
  email: string;
  phone: string;
  parentName: string;
  parentPhone: string;
  attendance: number;
  lastGrade: string;
  status: string;
  avatar: string;
}

interface ClassData {
  code: string;
  subject: string;
  students: number;
}

const StudentsDirectory = () => {
  const routes = all_routes;
  const [selectedClass, setSelectedClass] = useState("10A");
  const [searchTerm, setSearchTerm] = useState("");
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  // Mock data for classes
  const classesData: ClassData[] = [
    { code: "10A", subject: "Mathematics", students: 32 },
    { code: "11B", subject: "Physics", students: 28 },
    { code: "9C", subject: "Mathematics", students: 30 },
    { code: "12A", subject: "Advanced Math", students: 25 },
    { code: "8B", subject: "Basic Math", students: 35 },
  ];

  // Mock students data
  const studentsData: Record<string, Student[]> = {
    "10A": [
      {
        id: "1",
        name: "Emma Johnson",
        rollNo: "001",
        email: "emma.johnson@school.edu",
        phone: "+1 234-567-8901",
        parentName: "Robert Johnson",
        parentPhone: "+1 234-567-8902",
        attendance: 94.5,
        lastGrade: "A-",
        status: "active",
        avatar: "E",
      },
      {
        id: "2",
        name: "Michael Smith",
        rollNo: "002",
        email: "michael.smith@school.edu",
        phone: "+1 234-567-8903",
        parentName: "Sarah Smith",
        parentPhone: "+1 234-567-8904",
        attendance: 88.2,
        lastGrade: "B+",
        status: "active",
        avatar: "M",
      },
      {
        id: "3",
        name: "Sofia Rodriguez",
        rollNo: "003",
        email: "sofia.rodriguez@school.edu",
        phone: "+1 234-567-8905",
        parentName: "Carlos Rodriguez",
        parentPhone: "+1 234-567-8906",
        attendance: 96.8,
        lastGrade: "A",
        status: "active",
        avatar: "S",
      },
      {
        id: "4",
        name: "James Wilson",
        rollNo: "004",
        email: "james.wilson@school.edu",
        phone: "+1 234-567-8907",
        parentName: "Lisa Wilson",
        parentPhone: "+1 234-567-8908",
        attendance: 76.5,
        lastGrade: "C+",
        status: "attention",
        avatar: "J",
      },
    ],
    "11B": [
      {
        id: "5",
        name: "Aisha Patel",
        rollNo: "001",
        email: "aisha.patel@school.edu",
        phone: "+1 234-567-8909",
        parentName: "Raj Patel",
        parentPhone: "+1 234-567-8910",
        attendance: 92.3,
        lastGrade: "A-",
        status: "active",
        avatar: "A",
      },
    ],
  };

  const currentStudents = studentsData[selectedClass] || [];
  const filteredStudents = currentStudents.filter(
    (student: Student) =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.rollNo.includes(searchTerm)
  );

  const getAttendanceColor = (attendance: number): string => {
    if (attendance >= 90) return "success";
    if (attendance >= 80) return "warning";
    return "danger";
  };

  const getGradeColor = (grade: string): string => {
    if (grade.includes("A")) return "success";
    if (grade.includes("B")) return "info";
    if (grade.includes("C")) return "warning";
    return "danger";
  };

  const handleStudentClick = (student: Student) => {
    setSelectedStudent(student);
    setShowStudentModal(true);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Students Directory</h3>
              <p className="text-muted mb-0">Students organized by class</p>
            </div>
          </div>

          {/* Class Selection */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <label className="form-label">Select Class</label>
                  <select
                    className="form-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    {classesData.map((cls) => (
                      <option key={cls.code} value={cls.code}>
                        {cls.code} - {cls.subject} ({cls.students} students)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-8">
                  <label className="form-label">Search Students</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name or roll number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="ti ti-search position-absolute top-50 end-0 translate-middle-y me-3"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Class Summary */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <h4 className="text-primary">{currentStudents.length}</h4>
                  <p className="mb-0">Total Students</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <h4 className="text-success">
                    {
                      currentStudents.filter(
                        (s: Student) => s.status === "active"
                      ).length
                    }
                  </h4>
                  <p className="mb-0">Active</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <h4 className="text-warning">
                    {
                      currentStudents.filter(
                        (s: Student) => s.status === "attention"
                      ).length
                    }
                  </h4>
                  <p className="mb-0">Need Attention</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-info-light">
                <div className="card-body text-center">
                  <h4 className="text-info">
                    {Math.round(
                      currentStudents.reduce(
                        (sum: number, s: Student) => sum + s.attendance,
                        0
                      ) / currentStudents.length
                    ) || 0}
                    %
                  </h4>
                  <p className="mb-0">Avg Attendance</p>
                </div>
              </div>
            </div>
          </div>

          {/* Students List */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                Class {selectedClass} -{" "}
                {classesData.find((c) => c.code === selectedClass)?.subject}
              </h5>
            </div>
            <div className="card-body">
              {filteredStudents.length > 0 ? (
                <div className="row g-3">
                  {filteredStudents.map((student: Student) => (
                    <div key={student.id} className="col-lg-6 col-xl-4">
                      <div
                        className="card border hover-card cursor-pointer"
                        onClick={() => handleStudentClick(student)}
                      >
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="avatar avatar-lg bg-primary me-3">
                              <span className="text-white fw-bold">
                                {student.avatar}
                              </span>
                            </div>
                            <div className="flex-fill">
                              <h6 className="mb-1">{student.name}</h6>
                              <p className="text-muted mb-0">
                                Roll No: {student.rollNo}
                              </p>
                            </div>
                            <div
                              className={`badge bg-${
                                student.status === "active"
                                  ? "success"
                                  : "warning"
                              }`}
                            >
                              {student.status}
                            </div>
                          </div>

                          <div className="row text-center">
                            <div className="col-6">
                              <div
                                className={`text-${getAttendanceColor(
                                  student.attendance
                                )}`}
                              >
                                <strong>{student.attendance}%</strong>
                                <p className="mb-0 small text-muted">
                                  Attendance
                                </p>
                              </div>
                            </div>
                            <div className="col-6">
                              <div
                                className={`text-${getGradeColor(
                                  student.lastGrade
                                )}`}
                              >
                                <strong>{student.lastGrade}</strong>
                                <p className="mb-0 small text-muted">
                                  Last Grade
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="d-flex gap-1 mt-3">
                            <Link
                              to={routes.attendanceTracker}
                              className="btn btn-success btn-sm flex-fill"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="ti ti-user-check"></i>
                            </Link>
                            <Link
                              to={routes.gradeEntry}
                              className="btn btn-primary btn-sm flex-fill"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="ti ti-clipboard-list"></i>
                            </Link>
                            <Link
                              to={routes.alertParents}
                              className="btn btn-warning btn-sm flex-fill"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="ti ti-bell"></i>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="avatar avatar-xl bg-secondary-transparent mb-3">
                    <i className="ti ti-users fs-1 text-secondary"></i>
                  </div>
                  <h5>No students found</h5>
                  <p className="text-muted">
                    {searchTerm
                      ? "Try adjusting your search criteria."
                      : "No students in this class."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Student Detail Modal */}
      {showStudentModal && selectedStudent && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Student Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowStudentModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-4 text-center mb-3">
                    <div className="avatar avatar-xl bg-primary mb-3">
                      <span className="text-white fw-bold fs-1">
                        {selectedStudent.avatar}
                      </span>
                    </div>
                    <h5>{selectedStudent.name}</h5>
                    <p className="text-muted">
                      Roll No: {selectedStudent.rollNo}
                    </p>
                    <div
                      className={`badge bg-${
                        selectedStudent.status === "active"
                          ? "success"
                          : "warning"
                      }`}
                    >
                      {selectedStudent.status}
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label small text-muted">
                          Email
                        </label>
                        <p className="mb-0">{selectedStudent.email}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small text-muted">
                          Phone
                        </label>
                        <p className="mb-0">{selectedStudent.phone}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small text-muted">
                          Parent Name
                        </label>
                        <p className="mb-0">{selectedStudent.parentName}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small text-muted">
                          Parent Phone
                        </label>
                        <p className="mb-0">{selectedStudent.parentPhone}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small text-muted">
                          Attendance
                        </label>
                        <p
                          className={`mb-0 text-${getAttendanceColor(
                            selectedStudent.attendance
                          )}`}
                        >
                          <strong>{selectedStudent.attendance}%</strong>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small text-muted">
                          Last Grade
                        </label>
                        <p
                          className={`mb-0 text-${getGradeColor(
                            selectedStudent.lastGrade
                          )}`}
                        >
                          <strong>{selectedStudent.lastGrade}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="d-flex gap-2 w-100">
                  <Link
                    to={routes.attendanceTracker}
                    className="btn btn-success"
                  >
                    <i className="ti ti-user-check me-2"></i>Mark Attendance
                  </Link>
                  <Link to={routes.gradeEntry} className="btn btn-primary">
                    <i className="ti ti-clipboard-list me-2"></i>Enter Grade
                  </Link>
                  <Link to={routes.alertParents} className="btn btn-warning">
                    <i className="ti ti-bell me-2"></i>Alert Parent
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentsDirectory;
